import type { OfflineErrorKind, SyncProgress, SyncResult } from "@novorender/api";

import type { OfflineDownloadErrorCode, OfflineDownloadStatusPayload } from "types/events";
import { assertNever } from "utils/assertNever";

/**
 * The host's `offlineDownloadStatus` events for a full sync, as the published Procore contract
 * maps the SDK's signals onto them.
 */

export type DownloadErrorCopyKey = "notEnoughSpaceError" | "networkDownloadError" | "genericDownloadError";

/** What one status change has to report; the emitter stamps the identity fields. */
export type OfflineDownloadStatusUpdate = Omit<
    OfflineDownloadStatusPayload,
    "sceneId" | "sceneVersionId" | "sceneSliceId" | "source"
>;

export function getOfflineDownloadErrorMessage(
    kind: OfflineErrorKind,
    translate: (key: DownloadErrorCopyKey) => string,
): string {
    switch (kind) {
        case "quotaExceeded":
            return translate("notEnoughSpaceError");
        case "offline":
            return translate("networkDownloadError");
        case "downloadFailed":
        case "invalidManifest":
        case "io":
        case "busy":
        case "captureFailed":
        case "unknown":
            return translate("genericDownloadError");
        default:
            return assertNever(kind);
    }
}

/**
 * Locale-independent counterpart of {@link getOfflineDownloadErrorMessage}, for the host. The
 * published code set is narrower than the SDK's error kinds: the rest are `"unknown"` until the
 * host type gains codes.
 */
export function getOfflineDownloadErrorCode(kind: OfflineErrorKind): OfflineDownloadErrorCode {
    switch (kind) {
        case "quotaExceeded":
            return "quotaExceeded";
        case "offline":
            return "offline";
        case "downloadFailed":
            return "downloadFailed";
        case "invalidManifest":
        case "io":
        case "busy":
        case "captureFailed":
        case "unknown":
            return "unknown";
        default:
            return assertNever(kind);
    }
}

/**
 * Turns one full sync's signals into the host events the published contract promises:
 * `"scanning"` on the first scan tick, `"synchronizing"` once on the first tick past the scan,
 * and one terminal status from the result. A pause is reported as `"aborted"`, the contract's
 * string for it. A resumed sync is a new one, and reports from `"scanning"` again.
 */
export function createFullSyncHostReporter(
    emit: (update: OfflineDownloadStatusUpdate) => void,
    translate: (key: DownloadErrorCopyKey) => string,
) {
    let reported: "nothing" | "scanning" | "synchronizing" = "nothing";

    return {
        progress(progress: SyncProgress) {
            if (progress.phase === "scan") {
                if (reported === "nothing") {
                    reported = "scanning";
                    emit({ status: "scanning" });
                }
            } else if (reported !== "synchronizing") {
                reported = "synchronizing";
                emit({ status: "synchronizing" });
            }
        },
        done(result: SyncResult) {
            switch (result.kind) {
                case "completed":
                    emit({ status: "synchronized" });
                    return;
                case "paused":
                    emit({ status: "aborted" });
                    return;
                case "failed":
                    emit({
                        status: "error",
                        error: getOfflineDownloadErrorMessage(result.error.kind, translate),
                        errorCode: getOfflineDownloadErrorCode(result.error.kind),
                    });
                    return;
                default:
                    assertNever(result);
            }
        },
    };
}
